// Native-only: reports the installed binary version to the backend and keeps
// the "update available" status (yellow bar in Shell) in sync.
import { Capacitor } from "@capacitor/core";
import { api } from "../api";

const RECHECK_MS = 6 * 60 * 60 * 1000;
let status = null;
const listeners = new Set();
let timer = null;
let appListener = null;

export const isNativeApp = () => Capacitor.isNativePlatform();

export function subscribeAppVersion(fn) {
  listeners.add(fn);
  fn(status);
  return () => listeners.delete(fn);
}

function emit(next) {
  status = next;
  listeners.forEach((fn) => fn(status));
}
if (typeof window !== "undefined") window.__setAppVersionStatus = emit; // debug hook (web preview)

export async function checkAppVersion() {
  if (!isNativeApp()) return null;
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    const plat = Capacitor.getPlatform();
    const hasSession = !!(sessionStorage.getItem("pmf_token") || localStorage.getItem("pmf_token"));
    const { data } = hasSession
      ? await api.post("/app/version", { platform: plat, version: info.version, build: info.build })
      : await api.get("/app/version/latest", { params: { platform: plat, version: info.version } });
    emit(data);
    return data;
  } catch (e) {
    return status;
  }
}

export async function startAppVersionWatch() {
  if (!isNativeApp() || timer) return;
  checkAppVersion();
  timer = setInterval(checkAppVersion, RECHECK_MS);
  try {
    const { App } = await import("@capacitor/app");
    appListener = await App.addListener("appStateChange", ({ isActive }) => { if (isActive) checkAppVersion(); });
  } catch (e) { /* web */ }
}

export function stopAppVersionWatch() {
  if (timer) clearInterval(timer);
  timer = null;
  try { appListener && appListener.remove(); } catch (e) {}
  appListener = null;
  emit(null);
}

const STORE_HTTPS = {
  ios: "https://apps.apple.com/us/app/onsitecomms/id6803729480",
  android: "https://play.google.com/store/apps/details?id=com.onsitecomms.app",
};
const STORE_SCHEME = {
  ios: "itms-apps://apps.apple.com/us/app/onsitecomms/id6803729480",
  android: "market://details?id=com.onsitecomms.app",
};

export function openStore(platform) {
  return openStoreUrl(platform, null);
}

/** Opens the store listing (custom https URL from the Master when given), store app first. */
export async function openStoreUrl(platform, httpsUrl) {
  const plat = platform || Capacitor.getPlatform();
  const https = httpsUrl || STORE_HTTPS[plat] || STORE_HTTPS.ios;
  if (!STORE_SCHEME[plat]) {
    window.open(https, "_blank");
    return;
  }
  // derive the store-app deep link from the https listing (custom id/package supported)
  let scheme = STORE_SCHEME[plat];
  if (httpsUrl) {
    if (plat === "ios") { const m = httpsUrl.match(/id(\d+)/); if (m) scheme = `itms-apps://apps.apple.com/app/id${m[1]}`; }
    if (plat === "android") { const m = httpsUrl.match(/[?&]id=([\w.]+)/); if (m) scheme = `market://details?id=${m[1]}`; }
  }
  const hiddenAt = Date.now();
  let left = false;
  const onHide = () => { left = true; };
  document.addEventListener("visibilitychange", onHide);
  window.location.href = scheme;
  // Store app did not take over -> fall back to the https listing in the system browser
  setTimeout(async () => {
    document.removeEventListener("visibilitychange", onHide);
    if (left || document.visibilityState === "hidden" || Date.now() - hiddenAt > 4000) return;
    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url: https });
    } catch (e) { window.open(https, "_blank"); }
  }, 1500);
}
